import React, { useState } from 'react';
import { Mic, Loader2, CheckCircle2, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { track } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  event_type: '',
  preferred_date: '',
  location: '',
  message: '',
};

export const BookTheShowButton = ({ showTitle }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // null | {inquiry_id}
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const resetAndClose = () => {
    setOpen(false);
    setTimeout(() => {
      setForm(initialForm);
      setSubmitted(null);
      setError('');
    }, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Name, email, and a short message are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, show: showTitle }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Something went wrong. Please try again.');
      }
      const data = await res.json();
      setSubmitted({ inquiry_id: data.inquiry_id });
      track('booking_submitted', {
        show: showTitle,
        location: form.location || 'unspecified',
      });
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : resetAndClose())}>
      <DialogTrigger asChild>
        <Button
          className="w-full bg-accent-red hover:bg-accent-red-dark text-white font-black tracking-[0.18em] uppercase py-6 transition-all duration-300 hover:shadow-2xl hover:shadow-accent-red/40"
          data-testid={`book-the-show-btn-${showTitle}`}
        >
          <Mic className="mr-2" size={18} />
          Book The Show
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-gunmetal-900 border border-accent-red/40 text-steel-100 max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-black tracking-widest uppercase text-accent-red">
            Book The 40-Min Show
          </DialogTitle>
          <DialogDescription className="text-steel-400">
            {showTitle} — Raw. Real. Unfiltered. Tell us where and when.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-10 text-center space-y-4" data-testid="booking-success">
            <CheckCircle2 className="mx-auto text-accent-red" size={56} />
            <h3 className="text-xl font-bold tracking-wide">Inquiry locked in.</h3>
            <p className="text-steel-400">
              Reference:{' '}
              <span className="font-mono text-amber-400">{submitted.inquiry_id}</span>
            </p>
            <p className="text-steel-400">
              We'll reach out from{' '}
              <span className="text-steel-200">info@mindesignenterprises.com</span> shortly.
            </p>
            <Button
              className="bg-accent-red hover:bg-accent-red-dark text-white"
              onClick={resetAndClose}
              data-testid="booking-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="b-name" className="text-xs tracking-widest text-steel-400 uppercase">
                  Name *
                </Label>
                <Input
                  id="b-name"
                  value={form.name}
                  onChange={update('name')}
                  className="bg-gunmetal-800 border-steel-700 text-steel-100"
                  required
                  data-testid="booking-input-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-email" className="text-xs tracking-widest text-steel-400 uppercase">
                  Email *
                </Label>
                <Input
                  id="b-email"
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  className="bg-gunmetal-800 border-steel-700 text-steel-100"
                  required
                  data-testid="booking-input-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-phone" className="text-xs tracking-widest text-steel-400 uppercase">
                  Phone
                </Label>
                <Input
                  id="b-phone"
                  value={form.phone}
                  onChange={update('phone')}
                  className="bg-gunmetal-800 border-steel-700 text-steel-100"
                  data-testid="booking-input-phone"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-org" className="text-xs tracking-widest text-steel-400 uppercase">
                  Organization
                </Label>
                <Input
                  id="b-org"
                  value={form.organization}
                  onChange={update('organization')}
                  className="bg-gunmetal-800 border-steel-700 text-steel-100"
                  data-testid="booking-input-org"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-type" className="text-xs tracking-widest text-steel-400 uppercase">
                  Event Type
                </Label>
                <Input
                  id="b-type"
                  placeholder="Jail, school, church, festival…"
                  value={form.event_type}
                  onChange={update('event_type')}
                  className="bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600"
                  data-testid="booking-input-type"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="b-date" className="text-xs tracking-widest text-steel-400 uppercase">
                  Preferred Date
                </Label>
                <Input
                  id="b-date"
                  placeholder="YYYY-MM-DD or 'flexible'"
                  value={form.preferred_date}
                  onChange={update('preferred_date')}
                  className="bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600"
                  data-testid="booking-input-date"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="b-loc" className="text-xs tracking-widest text-steel-400 uppercase">
                Location
              </Label>
              <Input
                id="b-loc"
                placeholder="City, State"
                value={form.location}
                onChange={update('location')}
                className="bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600"
                data-testid="booking-input-location"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="b-msg" className="text-xs tracking-widest text-steel-400 uppercase">
                Tell us about the audience *
              </Label>
              <Textarea
                id="b-msg"
                rows={4}
                placeholder="Who will be in the room? What do you need from the 40 minutes?"
                value={form.message}
                onChange={update('message')}
                className="bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600"
                required
                data-testid="booking-input-message"
              />
            </div>

            {error && (
              <div
                className="text-sm text-accent-red bg-accent-red/10 border border-accent-red/40 rounded-md px-3 py-2"
                data-testid="booking-error"
              >
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={resetAndClose}
                className="text-steel-400 hover:text-steel-100"
                data-testid="booking-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-accent-red hover:bg-accent-red-dark text-white font-bold tracking-wider"
                data-testid="booking-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="mr-2" size={16} /> Submit Inquiry
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookTheShowButton;
